import React, { useEffect, useState, useRef } from 'react';
import FeedbackModuleComponent from './FeedbackComponent';
import { captureException } from '@sentry/react';
import { handleListFeedback } from 'store/feedback/thunks';
import { useDispatch } from 'react-redux';
import { useSocket } from 'store/chat/selectors';
import { useCurrentUser } from 'store/users/selectors';

const FeedbackModuleAdmin = () => {
  const [feedbackData, setFeedbackData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { socketConnection } = useSocket();
  const currentUser = useCurrentUser();
  const socketRef = useRef(null);

  const fetchFeedbackData = async (offset = 0, limit = 100, search = '', showLoader = true, filters = {}) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      const result = await dispatch(handleListFeedback({ 
        offset, 
        limit, 
        search,
        status: filters.status || '',
        startDate: filters.startDate || '',
        endDate: filters.endDate || '',
        sortBy: filters.sortBy || 'createdAt',
        sortOrder: filters.sortOrder || 'desc'
      }));

      let data = [];
      let total = 0;
      
      if (result && result.data && result.data.data && Array.isArray(result.data.data)) {
        data = result.data.data;
        total = result.data.total || 0;
      } else if (result && Array.isArray(result.data)) {
        data = result.data;
        total = result.data.length;
      } else {
        console.error('Failed to fetch data. Result structure:', result);
        data = [];
        total = 0;
      }
      
      setFeedbackData(data);
      setTotalCount(total);
      return { data, total };
    } catch (error) {
      console.error('Error in fetchFeedbackData:', error);
      captureException(`Error fetching feedback data: ${error}`);
      setFeedbackData([]);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const handleRefresh = (offset = 0, limit = 100, search = '', filters = {}) => {
    fetchFeedbackData(offset, limit, search, true, filters);
  };

  const handleUpdateItem = (itemId, updatedFields) => {
    setFeedbackData((prevData) =>
      prevData.map((item) =>
        item.id === itemId ? { ...item, ...updatedFields } : item
      )
    );
  };

  useEffect(() => {
    fetchFeedbackData();
  }, []);

  // Listen for socket notifications for new feedback
  useEffect(() => {
    if (!socketConnection || !currentUser || currentUser.role !== 'owe_admin') {
      return;
    }

    socketRef.current = socketConnection;

    const handleSocketMessage = (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (data.code === 'feedback_notification' && data.data) {
          // Auto-refresh the feedback list in background without loading indicator
          // Note: We don't pass filters here to get all new feedback
          fetchFeedbackData(0, 100, '', false, {});
        }
      } catch (error) {
        console.error('Error parsing socket message:', error);
      }
    };

    socketConnection.addEventListener('message', handleSocketMessage);

    return () => {
      if (socketConnection) {
        socketConnection.removeEventListener('message', handleSocketMessage);
      }
    };
  }, [socketConnection, currentUser]);

  return (
    <FeedbackModuleComponent 
      feedbackData={feedbackData}
      loading={loading}
      onUpdateItem={handleUpdateItem}
      onRefresh={handleRefresh}
      totalCount={totalCount}
    />
  );
};

export default FeedbackModuleAdmin;

